// Transcript/ChatEntry shaping helpers for the chat decider. Split out of
// `app/chatDecider.ts` (C1 hotspot decomposition; verbatim). Pure and total
// over entry lists — no I/O, no clock, no React.

import type { AgentEntry, ChatEntry } from "../../components/Chat/types.js";

// The ids of agent bubbles still streaming (a `streamSeq` cursor present). These
// are the messages a stop fences: a later delta, or a durable reply reusing one
// of these ids, belongs to the turn the author just cancelled. Pure and total —
// no streaming bubble yields an empty list.
export const liveStreamingMessageIds = (
    conversation: readonly ChatEntry[],
): readonly string[] =>
    conversation
        .filter(
            (entry): entry is AgentEntry =>
                entry.type === "agent" && entry.streamSeq !== undefined,
        )
        .map((entry) => entry.id);

// Turn a still-streaming agent bubble into a durable one by dropping its
// `streamSeq` cursor; the streamed-so-far text is kept. Constructed
// field-by-field so `streamSeq` is structurally absent — no cast.
export const finalizeStreamedAgentEntry = (entry: AgentEntry): AgentEntry => ({
    type: "agent",
    id: entry.id,
    markdown: entry.markdown,
});

// Append `entry`, first dropping any existing entry that shares BOTH its id
// AND its type — the authoritative form of a message supplanting its own
// streamed placeholder. Scoped to `type` too (not id alone): the server can
// commit a closing `AgentMessageSent` and a `RequestInformation` under the
// SAME `agentMessageId` (a turn that ends on a form has no reply prose of its
// own), so an id-only filter would let the empty `agent` entry evict the
// unrelated `information_request` entry sharing that id — the blank-bubble
// class of bug (F43b). Pure and total: with no id+type twin present it is a
// plain append.
//
// ORDER (F53 sibling): a turn that ends on a form commits the closing agent
// MESSAGE (the agent's intro prose, e.g. "Innan jag sätter igång…") and the
// FORM under the SAME id — but the form is published LIVE mid-turn while the
// message only lands when the POST resolves, so a naive tail-append drops the
// intro prose AFTER the form it introduces (the inverted-order bug). When the
// entry being appended is the agent MESSAGE and an `information_request` (or its
// pending placeholder) with the same id is already present, splice the message
// in JUST BEFORE that form so the prose reads first — matching the durable
// reload projection ([message, form]).
export const replaceOrAppend = (
    conversation: readonly ChatEntry[],
    entry: ChatEntry,
): readonly ChatEntry[] => {
    const filtered = conversation.filter(
        (e) => !(e.id === entry.id && e.type === entry.type),
    );
    const formIndex = filtered.findIndex(
        (e) =>
            (e.type === "information_request" ||
                e.type === "information_request_pending") &&
            e.id === entry.id,
    );
    if (entry.type === "agent" && formIndex !== -1) {
        return [
            ...filtered.slice(0, formIndex),
            entry,
            ...filtered.slice(formIndex),
        ];
    }
    return [...filtered, entry];
};

// D50: a load taken while this client still drives a turn sees that turn open
// in the log, so the adapter projects an `unfinished` row for it. The live turn
// is running, not unfinished: drop those rows before merging. (A mid-log one
// returns with the next load after the turn settles.)
export const withoutUnfinishedRows = (
    entries: readonly ChatEntry[],
): readonly ChatEntry[] =>
    entries.filter(
        (entry) => entry.type !== "system" || entry.kind.type !== "unfinished",
    );

// Merge a freshly loaded transcript into an in-flight session's conversation.
// The loaded entries are the authoritative server history and win on ordering
// and content; any LOCAL entry with no counterpart in the load is a
// not-yet-persisted optimistic entry (the just-submitted author bubble, its
// activity trail, a streaming reply placeholder) and is appended AFTER the
// loaded history in local order — it is the newest turn, so it belongs last.
// With an empty load this returns the local entries unchanged, which is exactly
// what makes "an empty server load wipes the optimistic message" (dogfood P1.4)
// structurally impossible. Pure and total over entry lists.
//
// The counterpart test is TWO-LAYERED because author bubbles are the one entry
// kind that exists under DIFFERENT ids across the two authorities: the optimistic
// `turn_started` echo carries a CLIENT id (`local-author-N`), but when the server
// persists the very same words it mints its OWN `messageId`. Matching by id alone
// can't see they are the same message, so the client copy would survive as a
// phantom tail and the author bubble would render TWICE (the duplicate-author
// class of bug — a load carrying the persisted twin racing a not-yet-reconciled
// optimistic author). So authors additionally reconcile by TEXT: a loaded author whose id has
// no local twin is a persisted copy AVAILABLE to absorb one still-optimistic local
// author of the same text. Consuming those twins front-to-back over the local list
// drops the copy whose persisted twin has arrived while leaving the NEWEST
// unmatched optimistic author (the turn the server hasn't stored yet) in the tail
// — that is the P1.4 invariant. Only authors reconcile this way; other kinds
// (activity trail, streaming reply placeholder) key on id alone, so their
// coexistence with a persisted twin can never arise here.
export const mergeInFlightTranscript = (
    loaded: readonly ChatEntry[],
    local: readonly ChatEntry[],
): readonly ChatEntry[] => {
    const loadedIds = new Set(loaded.map((entry) => entry.id));
    const localIds = new Set(local.map((entry) => entry.id));
    // Loaded author bubbles with NO local id-twin: each is a persisted copy that a
    // differently-ided optimistic local author of the same text may be the un-
    // persisted echo of. A loaded author whose id DOES match a local entry is
    // already that entry's twin and must not absorb a distinct message reusing the
    // same words (an earlier "ok" must not silently swallow a newer "ok").
    const availableTwinTexts = new Map<string, number>();
    for (const entry of loaded)
        if (entry.type === "author" && !localIds.has(entry.id))
            availableTwinTexts.set(
                entry.text,
                (availableTwinTexts.get(entry.text) ?? 0) + 1,
            );
    const optimisticTail = local.filter((entry) => {
        if (loadedIds.has(entry.id)) return false; // persisted twin by id
        if (entry.type !== "author") return true; // only authors reconcile by text
        const twins = availableTwinTexts.get(entry.text) ?? 0;
        if (twins === 0) return true; // no persisted twin yet — keep (P1.4)
        availableTwinTexts.set(entry.text, twins - 1); // claim one twin, drop copy
        return false;
    });
    return [...loaded, ...optimisticTail];
};
